import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const CHANNELS = [
  { key: 'V1', label: 'Channel 1', color: '#73bf69', fill: 'rgba(115,191,105,0.18)', idx: 1 },
  { key: 'V2', label: 'Channel 2', color: '#fade2a', fill: 'rgba(250,222,42,0.18)',  idx: 2 },
  { key: 'V3', label: 'Channel 3', color: '#5794f2', fill: 'rgba(87,148,242,0.18)',  idx: 3 },
];

const makeOptions = (color) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: '#1e1e1e',
      borderColor: '#333',
      borderWidth: 1,
      titleColor: '#888',
      bodyColor: color,
      padding: 8,
      callbacks: {
        title: (items) => `${parseFloat(items[0]?.label || 0).toFixed(1)} Hz`,
        label: (item) => `${item.parsed.y.toFixed(4)}`,
      },
    },
  },
  scales: {
    x: {
      grid: { color: 'rgba(255,255,255,0.05)', drawTicks: false },
      border: { color: '#333' },
      ticks: {
        color: '#666',
        font: { size: 10, family: 'monospace' },
        maxTicksLimit: 8,
        callback: (val, idx, ticks) => {
          const lbl = ticks[idx]?.label;
          return lbl !== undefined ? parseFloat(lbl).toFixed(0) : '';
        },
      },
      title: { display: true, text: 'Frequency (Hz)', color: '#555', font: { size: 10 } },
    },
    y: {
      grid: { color: 'rgba(255,255,255,0.05)', drawTicks: false },
      border: { color: '#333' },
      ticks: {
        color: '#666',
        font: { size: 10, family: 'monospace' },
        maxTicksLimit: 6,
        callback: (v) => v.toFixed(3),
      },
      title: { display: true, text: 'Magnitude', color: '#555', font: { size: 10 } },
    },
  },
});

// ── Metric Card (matches old Vibration.js MetricCard) ──────────────────────
const MetricCard = ({ title, value, unit, color, icon }) => (
  <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-gray-700/50 hover:border-blue-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 hover:scale-105">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-gray-300 text-xs sm:text-sm font-medium">{title}</h3>
      {icon && <span className="text-base sm:text-lg">{icon}</span>}
    </div>
    <div className="flex items-baseline gap-1">
      <span className="text-xl sm:text-2xl font-bold" style={{ color }}>
        {typeof value === 'number' ? value.toFixed(2) : value}
      </span>
      <span className="text-gray-400 text-xs sm:text-sm">{unit}</span>
    </div>
    <div className="mt-2 w-full bg-gray-700 rounded-full h-1">
      <div
        className="h-1 rounded-full transition-all duration-500"
        style={{ width: `${Math.min(Math.abs(value) * 10, 100)}%`, background: color }}
      />
    </div>
  </div>
);

// ── Channel FFT Panel ──────────────────────────────────────────────────────
const ChannelPanel = ({ ch, date, isRealtime }) => {
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [stats, setStats]         = useState(null);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(true);
  const [lastUpdate, setLastUpdate] = useState('');
  const intervalRef               = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      let url = 'https://cmti-edge.online/mtcm/Backend/VibrationFFT.php';
      url += date
        ? `?date=${date}&channel=${ch.key}`
        : `?realtime=1&channel=${ch.key}`;

      const res  = await fetch(url);
      const json = await res.json();

      if (json.success && json.data) {
        const xs = (json.data.x || []).map((v) => parseFloat(v).toFixed(2));
        const ys = (json.data.y || []).map((v) => parseFloat(v));

        setChartData({
          labels: xs,
          datasets: [{
            data: ys,
            borderColor: ch.color,
            borderWidth: 1.5,
            backgroundColor: ch.fill,
            fill: true,
            pointRadius: 0,
            tension: 0.1,
          }],
        });

        if (ys.length) {
          const min = Math.min(...ys);
          const max = Math.max(...ys);
          const avg = ys.reduce((a, b) => a + b, 0) / ys.length;
          setStats({ min, max, avg, pts: ys.length, id: json.data.id || 'N/A', createdAt: json.data.created_at || '' });
        }
        setLastUpdate(new Date().toLocaleTimeString());
        setError('');
      } else {
        throw new Error(json.error || 'No data available');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [ch.key, ch.color, ch.fill, date]);

  useEffect(() => {
    fetchData();
    clearInterval(intervalRef.current);
    if (isRealtime) {
      intervalRef.current = setInterval(fetchData, 2000);
    }
    return () => clearInterval(intervalRef.current);
  }, [fetchData, isRealtime]);

  return (
    <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-700/50 mb-6 sm:mb-8">

      {/* Panel header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
        <div className="flex items-center gap-3">
          <span
            className="inline-block w-3 h-3 rounded-full animate-pulse"
            style={{ background: ch.color, boxShadow: `0 0 8px ${ch.color}` }}
          />
          <h2 className="text-base sm:text-xl font-bold text-white">
            {ch.label} — FFT Spectrum Analysis
          </h2>
        </div>

        {/* Controls row */}
        <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
          {/* Status chip */}
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full animate-pulse ${isRealtime ? 'bg-blue-400' : 'bg-yellow-400'}`} />
            <span className="text-gray-400 text-xs">{isRealtime ? 'Real-time' : 'Historical'}</span>
          </div>
          {lastUpdate && (
            <span className="text-gray-500 text-xs hidden sm:inline">Updated: {lastUpdate}</span>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/50 border border-red-500/50 rounded-lg p-3 mb-4">
          <p className="text-red-300 text-xs sm:text-sm">{error}</p>
        </div>
      )}

      {/* Chart */}
      <div className="relative bg-black/60 rounded-lg p-2 sm:p-4">
        {loading && (
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
            <div className="flex items-center gap-3">
              <div
                className="animate-spin rounded-full h-6 w-6 border-b-2"
                style={{ borderColor: ch.color }}
              />
              <span className="text-gray-400 text-xs sm:text-sm">Loading FFT data…</span>
            </div>
          </div>
        )}
        <div className="w-full h-48 sm:h-64 md:h-72">
          <Line data={chartData} options={makeOptions(ch.color)} />
        </div>
      </div>

      {/* Stats row — matches old "Plot meta" + Grafana stats */}
      {stats && (
        <div className="mt-3 flex flex-wrap gap-3 sm:gap-6 text-[11px] sm:text-xs font-mono">
          <span className="text-gray-500">
            Record ID: <span className="text-gray-300">{stats.id}</span>
          </span>
          <span className="text-gray-500">
            Min: <span className="text-[#73bf69] font-semibold">{stats.min.toFixed(4)}</span>
          </span>
          <span className="text-gray-500">
            Max: <span className="text-[#f2495c] font-semibold">{stats.max.toFixed(4)}</span>
          </span>
          <span className="text-gray-500">
            Avg: <span className="text-[#b2b5be] font-semibold">{stats.avg.toFixed(4)}</span>
          </span>
          <span className="text-gray-500">
            Pts: <span className="font-semibold" style={{ color: ch.color }}>{stats.pts}</span>
          </span>
          {stats.createdAt && (
            <span className="text-gray-600 sm:ml-auto">Created: {stats.createdAt}</span>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Dashboard ─────────────────────────────────────────────────────────
const FFTDashboard = () => {
  const [vibrationData, setVibrationData] = useState({ xg: 0, yg: 0, zg: 0, freq: 0, rpm: 0 });
  const [isConnected, setIsConnected]     = useState(false);
  const [isRealtime, setIsRealtime]       = useState(true);
  const [date, setDate]                   = useState('');
  const [error, setError]                 = useState('');

  // Fetch sensor metrics
  const fetchVibrationData = useCallback(async () => {
    try {
      const res  = await fetch('https://cmti-edge.online/mtcm/Backend/mtcmedge.php');
      const data = await res.json();
      if (data.success) {
        const freq = parseFloat(data.data.freq) || 0;
        setVibrationData({
          xg:  parseFloat(data.data.xg)  || 0,
          yg:  parseFloat(data.data.yg)  || 0,
          zg:  parseFloat(data.data.zg)  || 0,
          freq,
          rpm: freq * 60,
        });
        setIsConnected(true);
        setError('');
      } else {
        throw new Error(data.error || 'Failed to fetch vibration data');
      }
    } catch {
      setError('Failed to fetch vibration data');
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    fetchVibrationData();
    const iv = setInterval(fetchVibrationData, 1000);
    return () => clearInterval(iv);
  }, [fetchVibrationData]);

  const handleDateChange = (e) => {
    const val = e.target.value;
    setDate(val);
    if (val) setIsRealtime(false);
  };

  const toggleRealtime = () => {
    if (!isRealtime) { setDate(''); setIsRealtime(true); }
    else setIsRealtime(false);
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">

      {/* Animated background */}
      <div className="fixed inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(116,0,184,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(72,149,239,0.1),transparent_50%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">

        {/* ── Header ── */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-200 via-gray-400 to-gray-600 mb-3 px-2">
            Vibration Monitoring Dashboard
          </h1>
          <div className="flex items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
              <span className="text-gray-400">{isConnected ? 'Connected' : 'Disconnected'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-gray-400">3-Channel FFT Monitor</span>
            </div>
          </div>
        </div>

        {/* Global error */}
        {error && (
          <div className="bg-red-900/50 border border-red-500/50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-red-300 text-xs sm:text-sm">{error}</p>
          </div>
        )}

        {/* ── Primary metrics (Freq + RPM) ── */}
        <div className="grid grid-cols-2 gap-3 sm:gap-5 mb-4 sm:mb-6">
          <MetricCard title="Frequency" value={vibrationData.freq} unit="Hz"  color="#4895ef" icon="📈" />
          <MetricCard title="RPM"       value={vibrationData.rpm}  unit="RPM" color="#7b2ff7" icon="🔄" />
        </div>

        {/* ── Acceleration metrics (X / Y / Z) ── */}
        <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-8 sm:mb-10">
          <MetricCard title="X-Axis" value={vibrationData.xg} unit="g" color="#4895ef" icon="↔️" />
          <MetricCard title="Y-Axis" value={vibrationData.yg} unit="g" color="#7b2ff7" icon="↕️" />
          <MetricCard title="Z-Axis" value={vibrationData.zg} unit="g" color="#10b981" icon="🔄" />
        </div>

        {/* ── FFT Controls (shared across all panels) ── */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <button
            onClick={toggleRealtime}
            className={`px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 border ${
              isRealtime
                ? 'bg-blue-600 border-blue-500 text-white hover:bg-blue-500'
                : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {isRealtime ? '⏸ Stop Real-time' : '▶ Start Real-time'}
          </button>

          <div className="flex items-center gap-2">
            <label className="text-gray-300 text-xs sm:text-sm">Date:</label>
            <input
              type="date"
              value={date}
              onChange={handleDateChange}
              style={{ colorScheme: 'dark' }}
              className="bg-gray-800 border border-gray-600 rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 text-white text-xs sm:text-sm focus:outline-none focus:border-blue-500"
            />
          </div>

          <span className="text-gray-500 text-[11px] sm:text-xs">
            {isRealtime ? '⟳ Refreshing every 2s' : date ? `Historical: ${date}` : 'Paused'}
          </span>
        </div>

        {/* ── 3 Channel FFT Panels ── */}
        {CHANNELS.map((ch) => (
          <ChannelPanel
            key={ch.key}
            ch={ch}
            date={isRealtime ? '' : date}
            isRealtime={isRealtime}
          />
        ))}
      </div>

      {/* Floating particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full opacity-20"
            style={{
              background: CHANNELS[i % 3].color,
              left: `${(i * 17 + 5) % 100}%`,
              top:  `${(i * 23 + 10) % 100}%`,
              animation: `float ${3 + (i % 4)}s ease-in-out infinite`,
              animationDelay: `${(i % 5) * 0.4}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50%       { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </div>
  );
};

export default FFTDashboard;